import { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import { ShirtType, Team } from "../../services/shirts/types";
import ShirtModelService from "../../services/shirts/shirtModelService";
import { CustomParam } from "../../types/types";
import BackdropLoaderService from "../BackdropLoader/backdropLoaderService";
import ShirtsService from "../../services/shirts/shirtsService";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import FullFilterHandler from "../FullFilterHandler/FullFilterHandler";
import { Operations } from "../../services/api/api";
import SortButton, {
  SORT_DIRECTION,
  SORT_VALUE,
  SortElement,
} from "../Buttons/SortButton/SortButton";

interface ShopShirtModelFiltersProps {
  setShirts: Function;
  page: number;
  setLastPage: Function;
  setTotalFound: Function;
  itemsPerPage: number;
}

const ShopShirtModelFilters = ({
  setShirts,
  page,
  setLastPage,
  setTotalFound,
  itemsPerPage,
}: ShopShirtModelFiltersProps) => {
  const [team, setTeam] = useState<Team | null>(null);
  const [teamOptions, setTeamOptions] = useState<Array<Team> | undefined>(
    undefined
  );
  const [typeOptions, setTypeOptions] = useState<Array<ShirtType> | undefined>(
    undefined
  );
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [fullFilterValue, setFullFilterValue] = useState<
    CustomParam<string | number | SORT_DIRECTION | SORT_VALUE> | undefined
  >(undefined);
  const [orderBy, setOrderBy] = useState<SortElement>({
    value: SORT_VALUE.RECENTLY_ADDED,
    direction: SORT_DIRECTION.DESC,
  });

  const openPopover = Boolean(anchorEl);
  const id = openPopover ? "simple-popover" : undefined;

  const { getAllTeams, getAllTypes } = ShirtModelService();
  const { open: openBDL, close: closeBDL } = BackdropLoaderService();
  const { searchShopShirts } = ShirtsService();

  const handleOpenPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const search = () => {
    openBDL();
    let params: CustomParam<string | number> = fullFilterValue
      ? { ...fullFilterValue }
      : {};
    if (team) {
      params["team_id"] = { value: team.id, operation: Operations.EQUAL };
    }
    if (page) {
      params["page"] = { value: page, operation: Operations.EQUAL };
    }
    if (orderBy) {
      params["order_by"] = { value: orderBy.value, operation: Operations.NONE };
      params["order_direction"] = {
        value: orderBy.direction,
        operation: Operations.NONE,
      };
    }

    params["perPage"] = { value: itemsPerPage, operation: Operations.NONE };

    searchShopShirts(params).then((results) => {
      setShirts(results.shirts);
      setLastPage(results.lastPage);
      setTotalFound(results.total);
      setFullFilterValue(undefined);
      closeBDL();
    });
  };

  useEffect(() => {
    getAllTeams().then((teams) => {
      setTeamOptions(teams);
    });

    getAllTypes().then((types) => {
      setTypeOptions(types);
    });

    search();
  }, []);

  useEffect(() => {
    if (itemsPerPage) {
      search();
    }
  }, [itemsPerPage]);

  useEffect(() => {
    if (fullFilterValue) {
      handleClosePopover();
      search();
    }
  }, [fullFilterValue]);

  useEffect(() => {
    search();
  }, [orderBy, page]);

  useEffect(() => {
    if (itemsPerPage) {
      search();
    }
  }, [itemsPerPage]);

  return (
    <Box sx={{ width: "100%" }}>
      <Typography
        sx={{
          fontFamily: "Manrope",
          fontWeight: "700 !important",
          lineHeight: "1.2 !important",
          textAlign: "left",
          color: "#6f7d95 !important",
          fontSize: { xs: "1.4rem", md: "1.75rem" },
        }}
      >
        Shop
      </Typography>

      <Typography
        variant="whyText"
        sx={{
          fontWeight: "500 !important",
          color: "#6f7d95 !important",
          fontSize: "1rem !important",
          textAlign: "left",
          marginY: "1rem",
          lineHeight: "1.1 !important",
        }}
      >
        Browse shirts listings and auctions from eBay. More selling platforms
        will be added soon.
      </Typography>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          rowGap: "1rem",
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            width: {
              sm: "calc((100% - 100px) * 0.55)",
              xs: "100%",
            },
          }}
        >
          <Autocomplete
            value={team}
            fullWidth
            disablePortal
            getOptionLabel={(option: Team) => option.name}
            options={teamOptions ? teamOptions : []}
            disabled={!teamOptions}
            onChange={(event: any, newValue: Team | null) => {
              setTeam(newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                placeholder="Search Clubs"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "50px",
                    paddingRight: "0px !important",
                    backgroundColor: "white",
                    "& fieldset": {
                      border: "none",
                    },
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "#3c3c40 !important",
                    fontWeight: 800,
                    fontSize: "16px",
                    opacity: 1,
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        type="button"
                        sx={{ p: "10px" }}
                        aria-label="search"
                        onClick={search}
                      >
                        <SearchIcon
                          sx={{
                            fill: "unset !important",
                            fontSize: "20px !important",
                            fontWeight: "900 !important",
                          }}
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Box>
        <Box>
          <SortButton handleClick={setOrderBy} />
        </Box>
        <Box
          sx={{
            width: {
              sm: "calc((100% - 100px) * 0.4)",
              xs: "calc(100% - 120px)",
            },
          }}
        >
          <Button
            fullWidth
            type="button"
            sx={{
              textTransform: "capitalize",
              height: "50px",
              backgroundColor: "#fff",
            }}
            aria-label="sort"
            onClick={handleOpenPopover}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                paddingX: "1rem",
              }}
            >
              <Box>
                <Typography
                  sx={{
                    color: "#3c3c40 !important",
                    fontWeight: 800,
                    fontSize: "16px",
                  }}
                >
                  Filter
                </Typography>
              </Box>
              <Box>
                <TuneIcon sx={{ display: "flex", alignItems: "center" }} />
              </Box>
            </Box>
          </Button>
          <Popover
            id={id}
            open={openPopover}
            anchorEl={anchorEl}
            onClose={handleClosePopover}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            sx={{ borderRadius: "5px" }}
          >
            {typeOptions ? (
              <FullFilterHandler
                filterValue={fullFilterValue}
                setFilterValue={setFullFilterValue}
                typeOptions={typeOptions}
              />
            ) : (
              <CircularProgress color="secondary" />
            )}
          </Popover>
        </Box>
      </Box>
    </Box>
  );
};

export default ShopShirtModelFilters;
